import React, {useEffect} from 'react';
import {motion} from 'framer-motion';

const WelcomeVideo = () => {
  useEffect(() => {
    // Load the video suite player script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://videosuite-player-wrapper.vercel.app/assets';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.6}}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Welcome to the Future of Business Communications
          </motion.h2>
          <motion.p
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.1}}
            className="text-xl text-gray-600 max-w-4xl mx-auto mb-8"
          >
            Meet your new AI voice receptionist - a revolutionary technology that transforms how NYC and Hudson Valley businesses handle customer calls. Watch this introduction to discover how our intelligent voice assistant can elevate your customer service experience.
          </motion.p>
          <motion.div
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.2}}
            className="text-lg text-gray-700 max-w-5xl mx-auto leading-relaxed"
          >
            <p className="mb-4">
              This welcome video introduces you to the capabilities of our AI voice technology, demonstrating real conversations and showcasing the professional service quality that businesses across Manhattan, Brooklyn, Queens, the Bronx, and throughout the Hudson Valley region have come to rely on.
            </p>
            <p>
              From automated appointment scheduling to intelligent call routing, our AI voice receptionist handles complex customer interactions with natural language processing and human-like conversation abilities. Perfect for medical practices, law firms, real estate agencies, and service-based businesses of all sizes.
            </p>
          </motion.div>
        </div>

        {/* Video Container */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8, delay: 0.2}}
          className="max-w-5xl mx-auto"
        >
          <div className="bg-white rounded-2xl shadow-2xl p-4 md:p-8">
            <div className="iv-player_responsive_padding" style={{padding: '56.25% 0 0 0', position: 'relative'}} data-hash="6835c649d34c8">
              <div className="iv-player_responsive_wrapper" style={{height: '100%', left: 0, position: 'absolute', top: 0, width: '100%'}}>
                <div className="iv-player_embed iv-player_async_p2z7746nud videoFoam=true" style={{height: '100%', position: 'relative', width: '100%'}}>
                  <div className="iv-player_swatch" style={{height: '100%', left: 0, opacity: 0, overflow: 'hidden', position: 'absolute', top: 0, width: '100%'}}>
                    <img src="https://vz-5b49b1a0-718.b-cdn.net/caa748b8-0a9e-4dde-a65f-6bae1ec55b10/thumbnail.jpg" style={{filter: 'blur(5px)', height: '100%', objectFit: 'contain', width: '100%'}} alt="AI Voice Receptionist Welcome Video Thumbnail" aria-hidden="true" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Video Description */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.6, delay: 0.3}}
          className="mt-16 bg-white rounded-2xl p-8 shadow-lg"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            What You'll Learn in This Welcome Video
          </h3>
          <div className="grid md:grid-cols-3 gap-8 text-gray-700">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">AI Voice Capabilities</h4>
              <p>
                Discover how our AI voice receptionist handles real customer conversations with natural language processing, understanding context, intent, and emotional nuances in every interaction.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Integration Benefits</h4>
              <p>
                Learn how seamlessly our AI integrates with your existing PBX systems, VoIP infrastructure, and business tools to enhance rather than replace your current operations.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Business Impact</h4>
              <p>
                See real examples of how NYC and Hudson Valley businesses have transformed their customer service operations and improved satisfaction scores with our AI technology.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Call to Action Below Video */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.6, delay: 0.4}}
          className="text-center mt-12"
        >
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-white max-w-4xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Experience This Technology for Your Business?
            </h3>
            <p className="text-blue-100 mb-6 text-lg">
              After watching our welcome video, take the next step and call our live demo line to interact directly with our AI voice assistant. Experience firsthand the professional, intelligent call handling that's revolutionizing business communications.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+13322324531"
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold transition-colors duration-300 flex items-center gap-2"
              >
                📞 Try Live Demo: +1 332-232-4531
              </a>
              <button
                onClick={() => document.getElementById('pricing').scrollIntoView({behavior: 'smooth'})}
                className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-300"
              >
                View Pricing Plans
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WelcomeVideo;